import React from "react";
import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import analytics from "./services/analytics";

/**
 * Initialize Sentry for error tracking
 *
 * Sentry catches unhandled errors and React crashes, sending them to your
 * Sentry dashboard with stack traces, user info, and breadcrumbs showing
 * what happened before the crash.
 *
 * Set VITE_SENTRY_DSN in your environment or Vercel dashboard.
 */
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE, // 'development' or 'production'

    // Capture 10% of transactions for performance monitoring
    tracesSampleRate: 0.1,

    // Send user IP and browser info to help with debugging
    sendDefaultPii: true,

    // Filter out chunk loading errors (handled separately below)
    beforeSend(event) {
      const errorMessage = event.exception?.values?.[0]?.value || "";
      if (
        errorMessage.includes("Failed to fetch dynamically imported module") ||
        errorMessage.includes("Loading chunk")
      ) {
        return null; // Don't send chunk errors to Sentry (they trigger reloads)
      }
      return event;
    },
  });
}

// Initialize Mixpanel analytics early (before app renders)
analytics.init();

/**
 * Initialize Rehearsals (DeepPrediction) Session Replay
 *
 * This loads the session replay SDK that records user sessions for analysis.
 * It helps us understand how users interact with the app and identify UX issues.
 *
 * Requires VITE_REHEARSALS_API_KEY, VITE_REHEARSALS_ORG_ID, and VITE_REHEARSALS_ENABLED
 * to be set in environment variables.
 *
 * Docs: https://www.runrehearsals.com/docs/quickstart
 */
const initRehearsals = () => {
  const apiKey = import.meta.env.VITE_REHEARSALS_API_KEY;
  const orgId = import.meta.env.VITE_REHEARSALS_ORG_ID;
  const enabled = import.meta.env.VITE_REHEARSALS_ENABLED === "true";

  // Only initialize if enabled and credentials are configured
  if (!enabled || !apiKey || !orgId) {
    if (import.meta.env.DEV) {
      console.log("🎬 Rehearsals session replay disabled or not configured");
    }
    return;
  }

  // Set up the configuration object that the SDK reads
  (
    window as unknown as {
      deepPredictionSettings: { apiKey: string; organizationId: string };
    }
  ).deepPredictionSettings = {
    apiKey: apiKey,
    organizationId: orgId,
  };

  // Dynamically load the recorder script
  const script = document.createElement("script");
  script.src = "https://app.runrehearsals.com/recorder.js";
  script.async = true;
  script.onload = () => {
    if (import.meta.env.DEV) {
      console.log("🎬 Rehearsals session replay initialized");
    }
  };
  script.onerror = () => {
    console.warn("🎬 Failed to load Rehearsals session replay script");
  };
  document.head.appendChild(script);
};

// Initialize session replay (after analytics)
initRehearsals();

/**
 * Handle Vite chunk loading errors (stale chunks after deployments)
 *
 * When a new deployment changes chunk hashes, users with cached pages
 * may try to load old chunks that no longer exist. The server returns
 * HTML (SPA fallback) instead of JS, causing a MIME type error.
 *
 * Solution: Force page reload to get fresh chunk manifest
 */
window.addEventListener("vite:preloadError", (event) => {
  console.warn("Chunk loading error detected, reloading page...", event);
  event.preventDefault();
  window.location.reload();
});

/**
 * Handle dynamic import failures (catches "Failed to fetch dynamically imported module")
 *
 * This catches the actual import() failures that happen when lazy-loaded chunks
 * can't be fetched (stale hashes, network issues, etc.)
 */
window.addEventListener("unhandledrejection", (event) => {
  const errorMessage = event.reason?.message || String(event.reason);

  // Check if this is a chunk loading error
  if (
    errorMessage.includes("Failed to fetch dynamically imported module") ||
    errorMessage.includes("Loading chunk") ||
    errorMessage.includes("Loading CSS chunk")
  ) {
    console.warn("Dynamic import failed, reloading page...", event.reason);
    event.preventDefault();

    // Avoid infinite reload loops - only reload once per session
    const lastReload = sessionStorage.getItem("xlr8_chunk_reload");
    const now = Date.now();

    if (!lastReload || now - parseInt(lastReload) > 10000) {
      // Only reload if we haven't in the last 10 seconds
      sessionStorage.setItem("xlr8_chunk_reload", String(now));
      window.location.reload();
    }
  }
});

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

/**
 * Fallback UI shown when a React error boundary catches a crash
 */
const ErrorFallback = () => (
  <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <img
        src="/Logo-crop.png"
        alt="XLR8 Logo"
        className="w-20 h-20 mx-auto mb-6 object-contain"
      />
      <h1 className="text-xl font-bold text-gray-900 mb-2">
        Something went wrong
      </h1>
      <p className="text-gray-600 mb-6">
        We've been notified and are working on a fix. Try refreshing the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
      >
        Refresh Page
      </button>
    </div>
  </div>
);

root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
